import { useCallback, useEffect, useState } from "react";
import {
  ApprovalsService,
  WorkRequestsService,
} from "../../../.generated/api-client";
import type { WorkRequestStepOut } from "../../../.generated/api-client/models/WorkRequestStepOut";
import { apiErrorMessage, asArray, asRecord } from "../../services/api";
import { mapApprovalDetail } from "./mappers";
import type { ApprovalDetail } from "./types";

type ApprovalMessageApi = {
  success: (content: string) => void;
  warning: (content: string) => void;
  error: (content: string) => void;
};

type UseApprovalDetailActionsParams = {
  selectedId: string | null;
  message: ApprovalMessageApi;
  onActionCompleted: (requestId: number) => Promise<void>;
};

export function useApprovalDetailActions({
  selectedId,
  message,
  onActionCompleted,
}: UseApprovalDetailActionsParams) {
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [detail, setDetail] = useState<ApprovalDetail | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [returnOpen, setReturnOpen] = useState(false);
  const [returnReason, setReturnReason] = useState("");

  const loadDetail = useCallback(async (requestId: number) => {
    setDetailLoading(true);
    setDetailError(null);
    setDetail(null);
    try {
      const [detailResult, stepsResult] = await Promise.allSettled([
        WorkRequestsService.getWorkRequestDetailEndpointApiV1WorkRequestsRequestIdGet(
          {
            requestId,
          },
        ),
        ApprovalsService.listWorkRequestStepsEndpointApiV1WorkRequestsRequestIdStepsGet(
          {
            requestId,
          },
        ),
      ]);
      if (detailResult.status === "rejected") {
        throw detailResult.reason;
      }
      const stepsResponse =
        stepsResult.status === "fulfilled" ? stepsResult.value : null;
      const stepsData = asRecord(stepsResponse?.data);
      const steps = asArray(stepsData.items).map(
        (step) => step as WorkRequestStepOut,
      );
      setDetail(
        mapApprovalDetail(
          detailResult.value,
          steps,
          stepsResult.status === "rejected"
            ? `审批链读取失败：${apiErrorMessage(stepsResult.reason)}`
            : null,
        ),
      );
    } catch (err) {
      setDetailError(apiErrorMessage(err));
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedId === null) {
      setDetail(null);
      setDetailError(null);
      return;
    }
    const id = Number(selectedId);
    if (!Number.isFinite(id) || id <= 0) {
      return;
    }
    void loadDetail(id);
  }, [selectedId, loadDetail]);

  const approveSelected = useCallback(async () => {
    if (!detail) return;
    setActionLoading(true);
    try {
      await WorkRequestsService.approveWorkRequestEndpointApiV1WorkRequestsRequestIdApprovePatch(
        {
          requestId: detail.requestId,
          requestBody: { version: detail.version },
        },
      );
      message.success("已通过申请");
      await onActionCompleted(detail.requestId);
    } catch (err) {
      message.error(apiErrorMessage(err));
    } finally {
      setActionLoading(false);
    }
  }, [detail, message, onActionCompleted]);

  const rejectSelected = useCallback(async () => {
    if (!detail) return;
    const reason = rejectReason.trim();
    if (!reason) {
      message.warning("请填写驳回原因");
      return;
    }
    setActionLoading(true);
    try {
      await WorkRequestsService.rejectWorkRequestEndpointApiV1WorkRequestsRequestIdRejectPatch(
        {
          requestId: detail.requestId,
          requestBody: { version: detail.version, reject_reason: reason },
        },
      );
      message.success("已驳回申请");
      setRejectOpen(false);
      setRejectReason("");
      await onActionCompleted(detail.requestId);
    } catch (err) {
      message.error(apiErrorMessage(err));
    } finally {
      setActionLoading(false);
    }
  }, [detail, message, onActionCompleted, rejectReason]);

  const returnSelected = useCallback(async () => {
    if (!detail) return;
    const reason = returnReason.trim();
    if (!reason) {
      message.warning("请填写回退原因");
      return;
    }
    const activeStep = detail.steps.find((step) => step.status === "active");
    if (!activeStep) {
      message.warning("当前没有可回退的审批节点");
      return;
    }
    setActionLoading(true);
    try {
      await ApprovalsService.decideStepEndpointApiV1WorkRequestsRequestIdStepsStepIdDecidePost(
        {
          requestId: detail.requestId,
          stepId: activeStep.step_id,
          requestBody: {
            decision: "return",
            version: activeStep.version,
            comment: reason,
          },
        },
      );
      message.success("已退回申请");
      setReturnOpen(false);
      setReturnReason("");
      await onActionCompleted(detail.requestId);
    } catch (err) {
      message.error(apiErrorMessage(err));
    } finally {
      setActionLoading(false);
    }
  }, [detail, message, onActionCompleted, returnReason]);

  return {
    detail,
    detailError,
    detailLoading,
    actionLoading,
    rejectOpen,
    rejectReason,
    returnOpen,
    returnReason,
    setRejectOpen,
    setRejectReason,
    setReturnOpen,
    setReturnReason,
    loadDetail,
    approveSelected,
    rejectSelected,
    returnSelected,
  };
}
